/**
 * OrdersPage - Liste et suivi des commandes client
 * Filtres par statut + Détails commande en modal
 * Timeline: pending → confirmed → preparing → ready → delivering → completed
 */

import { useState, useEffect } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { useEcommerceCart } from '../../hooks/ecommerce/useEcommerceCart';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Order, OrderStatus, OrderRefund } from '../../types/ecommerce.types';
import { logger } from '../../utils/logger';
import { formatQty } from '../../utils/pos.utils';
import { formatJourRetrait } from '../../utils/shopOrdering';

interface Preorder {
  id: string;
  preorder_number: string;
  customer_name: string;
  pickup_date: string;
  status: 'PENDING' | 'CONFIRMED' | 'READY' | 'PICKED_UP' | 'CANCELLED';
  source: 'POS' | 'ECOMMERCE';
  notes?: string;
  created_at: string;
  is_editable?: boolean;
  items: Array<{
    id: string;
    product_id: string;
    quantity: number;
    product: {
      name: string;
    };
  }>;
}

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; icon: string }> = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: '⏳' },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-700', icon: '✓' },
  preparing: { label: 'En préparation', color: 'bg-purple-100 text-purple-700', icon: '📦' },
  ready: { label: 'Prête', color: 'bg-indigo-100 text-indigo-700', icon: '✅' },
  delivering: { label: 'En livraison', color: 'bg-cyan-100 text-cyan-700', icon: '🚚' },
  completed: { label: 'Livrée', color: 'bg-green-100 text-green-700', icon: '🎉' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: '✕' },
};

export default function OrdersPage() {
  useTitrePage('Mes commandes');
  const navigate = useNavigate();
  const { addItem } = useEcommerceCart();
  // Recommander : identifiant de la commande en cours de re-remplissage du panier
  const [reordering, setReordering] = useState<string | null>(null);

  /**
   * Remplir le panier avec les articles d'une commande passée — au prix et à la
   * disponibilité DU JOUR. Chaque article est revalidé contre le catalogue :
   * épuisé, retiré ou introuvable → listé au client, jamais ajouté en aveugle.
   */
  /** Reprise d'un paiement carte resté en attente. */
  const [paiementEnCours, setPaiementEnCours] = useState<string | null>(null);
  const reprendrePaiement = async (o: Order) => {
    if (paiementEnCours) return;
    setPaiementEnCours(o.id);
    try {
      const session = await ecommerceApi.stripe.createOrderPayment({
        order_id: o.id,
        amount_cents: Number(o.total_cents),
      });
      if (session.url) window.location.href = session.url;
      else alert('Impossible de démarrer le paiement. Réessayez dans un instant.');
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Impossible de démarrer le paiement. Réessayez dans un instant.');
    } finally {
      setPaiementEnCours(null);
    }
  };

  /**
   * Une commande retirée au comptoir n'a pas été « livrée ». Le serveur fait
   * déjà cette distinction dans ses emails ; l'écran client, lui, affichait
   * « 🎉 Livrée » sur une boutique dont tout le tunnel dit « retrait ».
   */
  const etatAffiche = (o: { status: string; delivery_option?: string }) =>
    o.status === 'completed' && o.delivery_option !== 'delivery'
      ? { ...STATUS_CONFIG.completed, label: 'Retirée', icon: '🏪' }
      : STATUS_CONFIG[o.status as keyof typeof STATUS_CONFIG];

  const recommanderCommande = async (order: Order) => {
    if (reordering) return;
    setReordering(order.id);
    const ajoutes: string[] = [];
    const ecartes: string[] = [];

    // Les consignes ne se recommandent pas : elles reviennent d'elles-mêmes
    // avec les bouteilles et les bocaux, calculées par le serveur.
    const avecProduit = order.lines.filter(l => l.product_id && (l as any).added_by !== 'consigne');
    for (const l of order.lines) {
      if (!l.product_id) ecartes.push(`${l.product_name} (article libre)`);
    }

    // Disponibilité : la MÊME question que le tunnel de commande. Elle compte
    // le rayon ET la réserve, et laisse passer les précommandes. Le test
    // précédent lisait `stock_quantity`, qui ne somme que le RAYON : un article
    // dont tout le stock dort en réserve était annoncé « épuisé » et retiré du
    // panier, alors que le catalogue le vend (même défaut que celui corrigé
    // sur le catalogue le 02/09). On ne passe pas `is_weighted` : le serveur
    // retombe sur la nature de la fiche, plus sûre que ce qu'on déduirait ici.
    const indisponibles = new Set<string>();
    try {
      const dispo = await ecommerceApi.orders.checkAvailability(
        avecProduit.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          quantity: Number(l.quantity) || 1,
          quantity_unit: l.quantity_unit,
        })),
      );
      for (const souci of dispo.issues) indisponibles.add(souci.product_id);
    } catch {
      // Vérification impossible : on n'écarte rien à tort, le tunnel de
      // commande refera le contrôle avant tout paiement.
    }

    for (const line of avecProduit) {
      try {
        const produit = await ecommerceApi.products.getProductById(line.product_id);
        if (!produit || produit.active === false) {
          ecartes.push(`${line.product_name} (plus au catalogue)`);
          continue;
        }
        if (indisponibles.has(line.product_id)) {
          ecartes.push(`${line.product_name} (épuisé pour le moment)`);
          continue;
        }

        const poidsLigne = Number(line.weight_grams) || 0;
        const quantiteLigne = Number(line.quantity) || 1;
        const poidsPiece = Number((produit as any).avg_weight_per_piece_grams) || 0;

        if (line.quantity_unit === 'pieces' && poidsLigne > 0) {
          // Commandé À LA PIÈCE : on recommande des PIÈCES. Reprendre le poids
          // total estimé comme un poids ferait revenir « 3 ananas » en
          // « 2,4 kg » — et sur un produit vendu à la pièce uniquement
          // (ananas, pastèque), ce format n'existe même pas à la vente.
          addItem(produit, {
            quantity: 1,
            addedBy: 'customer',
            weightGrams: poidsLigne,
            quantityUnit: 'pieces',
            pieceCount: quantiteLigne,
          });
        } else if (poidsLigne > 0 && (produit as any).ecom_pieces_only && poidsPiece > 0) {
          // La fiche est passée « à la pièce uniquement » depuis la commande :
          // on convertit plutôt que de remettre un format que la boutique ne
          // sert plus.
          const pieces = Math.max(1, Math.round(quantiteLigne / poidsPiece));
          addItem(produit, {
            quantity: 1,
            addedBy: 'customer',
            weightGrams: pieces * poidsPiece,
            quantityUnit: 'pieces',
            pieceCount: pieces,
          });
        } else if (poidsLigne > 0) {
          // Ligne au poids : `weight_grams` est la PORTION et `quantity` le
          // total en grammes. Ne reprendre que la portion divisait la commande
          // (deux barquettes de 500 g revenaient en une seule).
          const portions = Math.max(1, Math.round(quantiteLigne / poidsLigne));
          addItem(produit, {
            quantity: portions,
            addedBy: 'customer',
            weightGrams: poidsLigne,
            quantityUnit: 'grams',
          });
        } else {
          // Pain : même coupe que la dernière fois, si la fiche le permet encore
          // (`addItem` ne garde que les options proposées par la fiche du jour).
          addItem(produit, { quantity: quantiteLigne, addedBy: 'customer', prepOptions: (line as any).prep_options || [] });
        }
        ajoutes.push(line.product_name);
      } catch {
        ecartes.push(`${line.product_name} (plus au catalogue)`);
      }
    }

    setReordering(null);
    if (ajoutes.length === 0) {
      alert('Aucun article de cette commande n\'est disponible aujourd\'hui.');
      return;
    }
    const detail = ecartes.length
      ? `\n\nNon ajoutés :\n– ${ecartes.join('\n– ')}`
      : '';
    if (
      window.confirm(
        `${ajoutes.length} article${ajoutes.length > 1 ? 's' : ''} ajouté${ajoutes.length > 1 ? 's' : ''} au panier, au prix du jour.${detail}\n\nAller au panier ?`,
      )
    ) {
      navigate('/shop/cart');
    }
  };
  const [searchParams] = useSearchParams();
  const { customer, selectedTenantId } = useCustomer();

  const [activeTab, setActiveTab] = useState<'orders' | 'preorders'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [preorders, setPreorders] = useState<Preorder[]>([]);
  const [reconductId, setReconductId] = useState<string | null>(null);
  const [reconductDate, setReconductDate] = useState('');
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderRefunds, setOrderRefunds] = useState<OrderRefund[]>([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (!customer) {
      navigate('/shop/login');
      return;
    }

    // Message de succès si redirect depuis checkout
    if (searchParams.get('success') === 'true') {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }

    if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'preorders') {
      loadPreorders();
    }
  }, [customer, activeTab]);

  useEffect(() => {
    applyFilter();
  }, [orders, filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await ecommerceApi.orders.getCustomerOrders();
      setOrders(data);
    } catch (err) {
      logger.error('Erreur chargement commandes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPreorders = async () => {
    try {
      setLoading(true);

      if (!customer || !selectedTenantId) {
        logger.warn('[OrdersPage] Customer ou tenant non disponible');
        setPreorders([]);
        return;
      }

      logger.log('[OrdersPage] Chargement précommandes:', {
        customerId: customer.id,
        tenantId: selectedTenantId,
      });

      const response = await fetch('/api/preorders/customer/my-preorders', {
        headers: {
          'X-Tenant-ID': selectedTenantId,
          'X-Customer-ID': customer.id,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[OrdersPage] Erreur API précommandes:', response.status, errorText);
        throw new Error('Erreur chargement précommandes');
      }

      const data = await response.json();
      logger.log('[OrdersPage] Précommandes chargées:', data.length);
      setPreorders(data);
    } catch (err) {
      logger.error('[OrdersPage] Erreur chargement précommandes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Self-service précommandes (endpoints client sécurisés, Bearer)
  const preorderAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'X-Tenant-ID': selectedTenantId || '',
    Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}`,
  });

  const cancelPreorder = async (id: string) => {
    if (!window.confirm('Annuler cette précommande ?')) return;
    try {
      const r = await fetch(`/api/preorders/customer/${id}/cancel`, { method: 'PATCH', headers: preorderAuthHeaders() });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.message || 'Annulation impossible');
      }
      loadPreorders();
    } catch (err: any) {
      alert('❌ ' + (err.message || 'Annulation impossible'));
    }
  };

  const submitReconduct = async (id: string) => {
    if (!reconductDate) return;
    try {
      const r = await fetch(`/api/preorders/customer/${id}/reconduct`, {
        method: 'POST',
        headers: preorderAuthHeaders(),
        body: JSON.stringify({ pickup_date: reconductDate }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.message || 'Reconduction impossible');
      }
      setReconductId(null);
      setReconductDate('');
      loadPreorders();
    } catch (err: any) {
      alert('❌ ' + (err.message || 'Reconduction impossible'));
    }
  };

  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(o => o.status === filter));
    }
  };

  const loadOrderRefunds = async (orderId: string) => {
    logger.log('[OrdersPage] 🔍 Loading refunds for order:', orderId);
    try {
      setLoadingRefunds(true);
      const refunds = await ecommerceApi.orders.getOrderRefunds(orderId);
      logger.log('[OrdersPage] ✅ Refunds loaded:', refunds);
      logger.log('[OrdersPage] 📊 Number of refunds:', refunds?.length || 0);
      setOrderRefunds(refunds);
    } catch (err) {
      logger.error('[OrdersPage] ❌ Erreur chargement remboursements:', err);
      setOrderRefunds([]);
    } finally {
      setLoadingRefunds(false);
    }
  };

  useEffect(() => {
    logger.log('[OrdersPage] 🎯 useEffect triggered, selectedOrder:', selectedOrder?.order_number);
    if (selectedOrder) {
      logger.log('[OrdersPage] 🚀 Calling loadOrderRefunds with ID:', selectedOrder.id);
      loadOrderRefunds(selectedOrder.id);
    } else {
      logger.log('[OrdersPage] ⏹️ No selected order, clearing refunds');
      setOrderRefunds([]);
    }
  }, [selectedOrder]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusTimeline = (order: Order) => {
    const steps: OrderStatus[] = ['confirmed', 'preparing', 'ready', 'delivering', 'completed'];
    const currentIndex = steps.indexOf(order.status);

    return steps.map((step, index) => ({
      status: step,
      completed: index <= currentIndex,
      current: index === currentIndex,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la boutique
          </button>

          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold text-gray-900">
              {activeTab === 'orders' ? 'Mes commandes' : 'Mes précommandes'}
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  activeTab === 'orders'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Commandes
              </button>
              <button
                onClick={() => setActiveTab('preorders')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  activeTab === 'preorders'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📦 Précommandes
              </button>
            </div>
          </div>
        </div>

        {/* Message de succès */}
        {showSuccessMessage && (
          <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 mb-6 animate-pulse">
            <div className="flex items-center">
              <div className="text-4xl mr-4">🎉</div>
              <div>
                <div className="text-2xl font-bold text-green-700">Commande confirmée !</div>
                <div className="text-green-700">
                  Votre commande a été enregistrée avec succès. Vous recevrez une notification lorsqu'elle sera prête.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtres - Seulement pour les commandes */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Toutes ({orders.length})
            </button>
            <button
              onClick={() => setFilter('confirmed')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'confirmed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Confirmées
            </button>
            <button
              onClick={() => setFilter('preparing')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'preparing'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              En préparation
            </button>
            <button
              onClick={() => setFilter('ready')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'ready'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Prêtes
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Livrées
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                filter === 'cancelled'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Annulées
            </button>
          </div>
        </div>
        )}

        {/* Liste des commandes */}
        {activeTab === 'orders' && (
        loading ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4" />
            <p className="text-gray-600 text-xl">Chargement de vos commandes...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-2xl font-bold text-gray-700 mb-2">Aucune commande</p>
            <p className="text-gray-600 mb-6">
              {filter === 'all'
                ? 'Vous n\'avez pas encore passé de commande.'
                : `Aucune commande ${STATUS_CONFIG[filter].label.toLowerCase()}.`}
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 transition-colors"
            >
              Découvrir nos produits
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => {
              const statusConfig = etatAffiche(order);

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => {
                    logger.log('[OrdersPage] 👆 Order clicked:', order.order_number, 'ID:', order.id);
                    setSelectedOrder(order);
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        Commande #{order.order_number}
                      </div>
                      <div className="text-gray-600">
                        {formatDate(order.created_at)}
                      </div>
                      {/* Badge précommande liée */}
                      {order.linked_preorders && order.linked_preorders.length > 0 && (
                        <div className="mt-2 inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                          <span>🥖</span>
                          <span>+ {order.linked_preorders.length} précommande{order.linked_preorders.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                    <div className={`px-4 py-2 rounded-full font-bold ${statusConfig.color}`}>
                      {statusConfig.icon} {statusConfig.label}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-center border-t border-gray-200 pt-4">
                    <div>
                      <div className="text-gray-600 text-sm">Articles</div>
                      <div className="text-xl font-bold">{order.lines.length}</div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm">Total</div>
                      <div className="text-xl font-bold text-green-700">
                        {(Number(order.total_cents) / 100).toFixed(2)} €
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm">Mode</div>
                      <div className="text-xl font-bold">
                        {order.delivery_option === 'delivery' ? '🚚' : '🏪'}
                      </div>
                    </div>
                  </div>

                  {/* Bouton de suivi en temps réel pour les livraisons */}
                  {order.delivery_option === 'delivery' && order.status === 'delivering' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/shop/track/${order.id}`);
                      }}
                      className="w-full mt-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                      <span className="text-xl animate-pulse">📍</span>
                      Suivre ma livraison en temps réel
                    </button>
                  )}

                  {/* Recommander : re-remplit le panier avec les mêmes articles,
                      revalidés au prix et au stock du jour */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void recommanderCommande(order);
                    }}
                    disabled={reordering === order.id}
                    className="w-full mt-4 border-2 border-green-600 text-green-700 py-3 rounded-lg font-bold hover:bg-green-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    🔁 {reordering === order.id ? 'Vérification des articles…' : 'Recommander ces articles'}
                  </button>

                  {order.delivery_date && (
                    <div className="mt-4 pt-4 border-t border-gray-200 text-gray-700">
                      <span className="font-medium">
                        {order.delivery_option === 'delivery' ? 'Livraison' : 'Retrait'} prévue :
                      </span>{' '}
                      {formatJourRetrait(order.delivery_date)} {order.delivery_slot && `· ${order.delivery_slot}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
        )}

        {/* Liste des précommandes */}
        {activeTab === 'preorders' && (
        loading ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4" />
            <p className="text-gray-600 text-xl">Chargement de vos précommandes...</p>
          </div>
        ) : preorders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-2xl font-bold text-gray-700 mb-2">Aucune précommande</p>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore de précommande de pain.
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors"
            >
              Découvrir nos pains
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {preorders.map(preorder => (
              <div
                key={preorder.id}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border-l-4 border-purple-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      📦 Précommande #{preorder.preorder_number}
                    </div>
                    <div className="text-gray-600">
                      Commandé le {new Date(preorder.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full font-bold ${
                    preorder.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : preorder.status === 'CONFIRMED'
                      ? 'bg-blue-100 text-blue-800'
                      : preorder.status === 'READY'
                      ? 'bg-green-100 text-green-800'
                      : preorder.status === 'PICKED_UP'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {preorder.status === 'PENDING' && '⏳ En attente'}
                    {preorder.status === 'CONFIRMED' && '✓ Confirmé'}
                    {preorder.status === 'READY' && '✅ Prêt'}
                    {preorder.status === 'PICKED_UP' && '🎉 Récupéré'}
                    {preorder.status === 'CANCELLED' && '✕ Annulé'}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="text-purple-600 text-sm font-medium mb-1">Retrait prévu</div>
                    <div className="text-lg font-bold text-gray-900">
                      {new Date(preorder.pickup_date).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })}
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="text-purple-600 text-sm font-medium mb-1">Source</div>
                    <div className="text-lg font-bold text-gray-900">
                      {preorder.source === 'POS' ? '🏪 En magasin' : '🌐 En ligne'}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="font-medium text-gray-700 mb-2">Articles:</div>
                  <div className="space-y-1">
                    {preorder.items.map((item) => (
                      <div key={item.id} className="flex items-center gap-2 text-gray-600">
                        <span className="text-purple-600">•</span>
                        <span className="font-medium">{formatQty(item.quantity)}x</span>
                        <span>{item.product.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {preorder.notes && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="font-medium text-yellow-800 text-sm mb-1">Note:</div>
                    <div className="text-gray-700">{preorder.notes}</div>
                  </div>
                )}

                {preorder.is_editable && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
                    {reconductId === preorder.id ? (
                      <>
                        <input
                          type="date"
                          value={reconductDate}
                          onChange={(e) => setReconductDate(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                        <button
                          onClick={() => submitReconduct(preorder.id)}
                          className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium"
                        >
                          Confirmer la reconduction
                        </button>
                        <button
                          onClick={() => { setReconductId(null); setReconductDate(''); }}
                          className="px-3 py-2 text-gray-600 text-sm"
                        >
                          Fermer
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setReconductId(preorder.id); setReconductDate(''); }}
                          className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                        >
                          🔁 Reconduire
                        </button>
                        <button
                          onClick={() => cancelPreorder(preorder.id)}
                          className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium"
                        >
                          Annuler la précommande
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
        )}
      </div>

      {/* Modal détails commande */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6 overflow-y-auto"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header modal */}
            <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Commande #{selectedOrder.order_number}
                </h2>
                <p className="text-gray-600">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Timeline statut */}
              {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'pending' && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4">Suivi de votre commande</h3>
                  <div className="relative">
                    {getStatusTimeline(selectedOrder).map((step, index) => (
                      <div key={step.status} className="flex items-center mb-4 last:mb-0">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            step.completed
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {step.completed ? '✓' : index + 1}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className={`font-bold ${step.completed ? 'text-green-700' : 'text-gray-600'}`}>
                            {selectedOrder && step.status === 'completed'
                              ? etatAffiche(selectedOrder).label
                              : STATUS_CONFIG[step.status].label}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Informations livraison */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  {selectedOrder.delivery_option === 'delivery' ? '🚚 Livraison' : '🏪 Retrait magasin'}
                </h3>
                {selectedOrder.delivery_date && (
                  <div className="text-gray-700">
                    <span className="font-medium">Date :</span> {formatJourRetrait(selectedOrder.delivery_date)}
                  </div>
                )}
                {selectedOrder.delivery_slot && (
                  <div className="text-gray-700">
                    <span className="font-medium">Créneau :</span> {selectedOrder.delivery_slot}
                  </div>
                )}
                {selectedOrder.delivery_address && (
                  <div className="text-gray-700 mt-2">
                    <div className="font-medium">Adresse :</div>
                    <div>{selectedOrder.delivery_address.street}</div>
                    <div>
                      {selectedOrder.delivery_address.postal_code} {selectedOrder.delivery_address.city}
                    </div>
                  </div>
                )}
              </div>

              {/* Articles */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Articles commandés</h3>
                <div className="space-y-2">
                  {selectedOrder.lines.map(line => (
                    <div
                      key={line.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{line.product_name}{(line as any).prep_options?.includes('coupé') ? ' · coupé' : ''}</div>
                        {(line as any).lot_promo_percent ? (
                          <div className="text-xs font-semibold text-red-700">
                            🏷️ Anti-gaspi −{(line as any).lot_promo_percent} %
                            {(line as any).lot_expiry_date
                              ? ` · date du lot ${String((line as any).lot_expiry_date).slice(0, 10).split('-').reverse().join('/')}`
                              : ''}
                          </div>
                        ) : null}
                        <div className="text-sm text-gray-600">
                          Quantité : {formatQty(line.quantity)}
                        </div>
                      </div>
                      <div className="text-lg font-bold">
                        {(line.total_price_cents / 100).toFixed(2)} €
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totaux */}
              <div className="border-t-2 border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Sous-total :</span>
                  <span>{(Number(selectedOrder.subtotal_cents) / 100).toFixed(2)} €</span>
                </div>
                {selectedOrder.delivery_fee_cents > 0 && (
                  <div className="flex justify-between text-gray-700">
                    <span>Frais de livraison :</span>
                    <span>{(Number(selectedOrder.delivery_fee_cents) / 100).toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-bold text-green-700 pt-2 border-t">
                  <span>Total :</span>
                  <span>{(Number(selectedOrder.total_cents) / 100).toFixed(2)} €</span>
                </div>
              </div>

              {/* Paiement */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="font-bold">Mode de paiement</div>
                <div className="text-gray-700">
                  {selectedOrder.payment_method === 'wallet' && '💳 Mes FOLIES'}
                  {selectedOrder.payment_method === 'card' && '💳 Carte bancaire'}
                  {selectedOrder.payment_method === 'cash' && '🧾 Au retrait (espèces, chèques repas, Bancontact)'}
                </div>
                <div className={`mt-2 font-bold ${
                  selectedOrder.payment_status === 'paid' ? 'text-green-700' : 'text-yellow-600'
                }`}>
                  {selectedOrder.payment_status === 'paid' ? '✓ Payé' : selectedOrder.payment_method === 'cash' ? '🧾 À régler au retrait' : '⏳ En attente'}
                </div>
                {/* Reprise du paiement : sans ce bouton, un paiement Stripe
                    abandonné ou en échec laissait la commande bloquée « en
                    attente » jusqu'à son annulation automatique, sans aucun
                    écran pour la régler. */}
                {selectedOrder.payment_method === 'card' &&
                  selectedOrder.payment_status !== 'paid' &&
                  selectedOrder.status !== 'cancelled' && (
                    <button
                      type="button"
                      onClick={() => void reprendrePaiement(selectedOrder)}
                      disabled={paiementEnCours === selectedOrder.id}
                      className="mt-3 w-full px-4 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:opacity-50"
                    >
                      {paiementEnCours === selectedOrder.id
                        ? 'Redirection…'
                        : `Payer ${(Number(selectedOrder.total_cents) / 100).toFixed(2)} €`}
                    </button>
                  )}
              </div>

              {/* Remboursements */}
              {(() => {
                logger.log('[OrdersPage] 🎨 Rendering refunds section - loadingRefunds:', loadingRefunds, 'orderRefunds.length:', orderRefunds.length, 'orderRefunds:', orderRefunds);
                return null;
              })()}
              {loadingRefunds ? (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-gray-600">Chargement des remboursements...</div>
                </div>
              ) : orderRefunds.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-3 text-orange-700">💰 Remboursements</h3>
                  <div className="space-y-3">
                    {orderRefunds.map((refund) => (
                      <div
                        key={refund.id}
                        className={`p-4 rounded-lg border-2 ${
                          refund.status === 'approved'
                            ? 'bg-green-50 border-green-300'
                            : refund.status === 'rejected'
                            ? 'bg-red-50 border-red-300'
                            : 'bg-yellow-50 border-yellow-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-bold text-lg">
                              {refund.refund_number}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(refund.requested_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-700">
                              {(refund.amount_cents / 100).toFixed(2)} €
                            </div>
                            <div className={`text-sm font-bold ${
                              refund.status === 'approved'
                                ? 'text-green-700'
                                : refund.status === 'rejected'
                                ? 'text-red-700'
                                : 'text-yellow-700'
                            }`}>
                              {refund.status === 'approved' && '✓ Remboursé'}
                              {refund.status === 'rejected' && '✕ Rejeté'}
                              {refund.status === 'pending' && '⏳ En attente'}
                              {refund.status === 'completed' && '✓ Finalisé'}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1 text-sm">
                          <div className="flex items-start">
                            <span className="font-medium text-gray-700 min-w-[100px]">Type :</span>
                            <span className="text-gray-900">
                              {refund.refund_type === 'product_missing' && 'Produit manquant'}
                              {refund.refund_type === 'product_defective' && 'Produit défectueux'}
                              {refund.refund_type === 'weight_difference' && 'Différence de poids'}
                              {refund.refund_type === 'order_cancelled' && 'Commande annulée'}
                              {refund.refund_type === 'goodwill_gesture' && 'Geste commercial'}
                            </span>
                          </div>
                          <div className="flex items-start">
                            <span className="font-medium text-gray-700 min-w-[100px]">Motif :</span>
                            <span className="text-gray-900">{refund.reason}</span>
                          </div>
                          {refund.product_name && (
                            <div className="flex items-start">
                              <span className="font-medium text-gray-700 min-w-[100px]">Produit :</span>
                              <span className="text-gray-900">
                                {refund.product_name}
                                {refund.quantity_affected && ` (Qté: ${refund.quantity_affected})`}
                              </span>
                            </div>
                          )}
                          {refund.description && (
                            <div className="flex items-start">
                              <span className="font-medium text-gray-700 min-w-[100px]">Description :</span>
                              <span className="text-gray-900 italic">{refund.description}</span>
                            </div>
                          )}
                          {refund.approved_at && (
                            <div className="flex items-start mt-2 pt-2 border-t border-gray-300">
                              <span className="font-medium text-gray-700 min-w-[100px]">Approuvé le :</span>
                              <span className="text-gray-900">
                                {new Date(refund.approved_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                          )}
                        </div>

                        {refund.status === 'approved' && (
                          <div className="mt-3 p-2 bg-white rounded border border-green-200 text-sm text-green-800">
                            <strong>✓ Le montant a été crédité sur votre compte prépayé</strong>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Total des remboursements */}
                  {orderRefunds.filter(r => r.status === 'approved').length > 0 && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-100 to-green-50 rounded-lg border-2 border-green-400">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Total remboursé :</span>
                        <span className="text-2xl font-bold text-green-700">
                          {(orderRefunds
                            .filter(r => r.status === 'approved')
                            .reduce((sum, r) => sum + r.amount_cents, 0) / 100
                          ).toFixed(2)} €
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Crédité sur votre compte prépayé
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bouton de suivi en temps réel */}
              {selectedOrder.delivery_option === 'delivery' && selectedOrder.status === 'delivering' && (
                <button
                  onClick={() => navigate(`/shop/track/${selectedOrder.id}`)}
                  className="w-full mt-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  <span className="text-2xl animate-pulse">📍</span>
                  Suivre ma livraison en temps réel
                </button>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
